import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button, Chip, Label, ListBox, Select } from "@heroui/react";

import { PageLayout } from "@/shared/components/page-layout.ui.tsx";
import { useCities } from "@/features/city/use-cities.ts";
import {
  CourierLocation,
  courierName,
  hasLocation,
} from "@/features/courier-map/type.ts";
import { locationFreshness } from "@/features/courier-map/freshness.ts";
import { useCourierLocations } from "@/features/courier-map/use-courier-locations.ts";
import { CouriersMap } from "@/features/courier-map/ui/couriers-map.tsx";
import { formatDateTime } from "@/shared/helper/datetime.ts";

export const Route = createFileRoute("/dashboard/couriers-map")({
  component: RouteComponent,
});

const ALL_CITIES = "all";

const FRESHNESS_CHIP = {
  fresh: "success",
  stale: "warning",
  lost: "default",
} as const;

function CourierRow({
  courier,
  isSelected,
  onSelect,
}: {
  courier: CourierLocation;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const freshness = locationFreshness(courier.updatedAt);
  const located = hasLocation(courier);

  return (
    <button
      className={`flex flex-col gap-1 px-3 py-2 text-left border-b border-divider last:border-b-0 ${
        isSelected ? "bg-default-100" : "hover:bg-default-50"
      } ${located ? "" : "opacity-60"}`}
      disabled={!located}
      type="button"
      onClick={onSelect}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">{courierName(courier)}</span>
        <Chip color={FRESHNESS_CHIP[freshness.level]} size="sm" variant="soft">
          {freshness.label}
        </Chip>
      </div>
      <span className="text-xs text-default-500">
        {courier.phoneNumber ?? "—"}
      </span>
      <span className="text-xs text-default-400">
        Смена с {formatDateTime(courier.shiftOpen)}
      </span>
    </button>
  );
}

function RouteComponent() {
  const [cityKey, setCityKey] = useState<string>(ALL_CITIES);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const cityId = cityKey === ALL_CITIES ? undefined : Number(cityKey);
  const { data, isFetching, refetch } = useCourierLocations(cityId);
  const cities = useCities();

  const couriers = data.data ?? [];
  const located = useMemo(() => couriers.filter(hasLocation), [couriers]);

  const cityItems = [
    { key: ALL_CITIES, title: "Все города" },
    ...cities.data.data.map((city) => ({
      key: String(city.id),
      title: city.name,
    })),
  ];

  return (
    <PageLayout
      actions={
        <div className="flex flex-row items-end gap-2">
          <Select
            className="w-48"
            selectedKey={cityKey}
            onSelectionChange={(key) =>
              setCityKey((key as string) ?? ALL_CITIES)
            }
          >
            <Label>Город</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox items={cityItems}>
                {({ key, title }) => (
                  <ListBox.Item id={key} textValue={title}>
                    {title}
                  </ListBox.Item>
                )}
              </ListBox>
            </Select.Popover>
          </Select>

          <Button
            isPending={isFetching}
            variant="secondary"
            onPress={() => refetch()}
          >
            Обновить
          </Button>
        </div>
      }
      title="Карта курьеров"
    >
      <p className="px-4 text-sm text-default-500">
        Курьеры с открытой сменой. Приложение отправляет координату раз в
        минуту, точка хранится 5 минут — карта сама обновляется каждые 20
        секунд. На карте {located.length} из {couriers.length}.
      </p>

      <div className="flex flex-col-reverse lg:flex-row gap-3 px-4">
        <div className="lg:w-72 shrink-0 border border-divider rounded-lg overflow-y-auto max-h-[70vh]">
          {couriers.length === 0 ? (
            <p className="p-3 text-sm text-default-500">
              Нет курьеров на смене
            </p>
          ) : (
            couriers.map((courier) => (
              <CourierRow
                key={courier.userId}
                courier={courier}
                isSelected={courier.userId === selectedId}
                onSelect={() => setSelectedId(courier.userId)}
              />
            ))
          )}
        </div>

        <div className="flex-1 h-[70vh] border border-divider rounded-lg overflow-hidden">
          <CouriersMap
            couriers={located}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </div>
      </div>
    </PageLayout>
  );
}
